import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { SEMRESATTRS_SERVICE_NAME, SEMRESATTRS_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

export const setupTelemetry = () => {
    // Only initialize in browser environment
    if (typeof window === 'undefined') {
        console.log('🔍 OpenTelemetry: Skipping initialization - not in browser environment');
        return;
    }

    // Get OTLP endpoint from environment variable or default to localhost for development
    const otlpEndpoint = import.meta.env.VITE_OTLP_ENDPOINT || 'http://localhost:4318/v1/traces';
    
    // Skip telemetry if running on production without a configured endpoint
    const isProduction = window.location.hostname !== 'localhost' && !window.location.hostname.includes('.local');
    const hasValidEndpoint = otlpEndpoint && !otlpEndpoint.includes('localhost');
    
    if (isProduction && !hasValidEndpoint) {
        console.log('🔍 OpenTelemetry: Skipping initialization - production mode without configured collector endpoint');
        return;
    }

    console.log('🚀 OpenTelemetry: Starting initialization...');

    try {
        const resource = resourceFromAttributes({
            [SEMRESATTRS_SERVICE_NAME]: 'transactor-admin-frontend',
            [SEMRESATTRS_SERVICE_VERSION]: '1.0.0',
        });

        console.log('📦 OpenTelemetry: Resource created:', resource);

        // Create and configure OTLP exporter
        const otlpExporter = new OTLPTraceExporter({
            url: otlpEndpoint,
            headers: {},
        });

        console.log('📡 OpenTelemetry: OTLP Exporter created for:', otlpEndpoint);

        // Use BatchSpanProcessor for better performance
        const spanProcessor = new BatchSpanProcessor(otlpExporter, {
            scheduledDelayMillis: 500,
            exportTimeoutMillis: 30000,
            maxExportBatchSize: 512,
            maxQueueSize: 2048,
        });

        console.log('⚙️ OpenTelemetry: Span processor configured');

        const provider = new WebTracerProvider({
            resource,
            spanProcessors: [spanProcessor], // Pass processors in constructor
        });

        console.log('🏭 OpenTelemetry: WebTracerProvider created');

        provider.register();
        console.log('✅ OpenTelemetry: Provider registered');

        // Register network-focused instrumentations
        registerInstrumentations({
            instrumentations: [
                // Fetch API instrumentation for modern network calls
                new FetchInstrumentation({
                    propagateTraceHeaderCorsUrls: [
                        /http:\/\/localhost:5177.*/, // Backend API
                        /https?:\/\/.*/, // Allow all URLs for comprehensive tracing
                    ],
                    clearTimingResources: true,
                    // Add detailed network attributes to spans
                    applyCustomAttributesOnSpan: (span, request, result) => {
                        console.log('🔍 OpenTelemetry: Instrumenting fetch request:', request.url);
                        span.setAttribute('http.frontend', 'react-admin');
                        span.setAttribute('http.request.size', request.body ? JSON.stringify(request.body).length : 0);

                        if (result instanceof Response) {
                            span.setAttribute('http.response.size', result.headers.get('content-length') || 'unknown');
                            span.setAttribute('http.response.content_type', result.headers.get('content-type') || 'unknown');
                        }
                    },
                    // Ignore internal requests that aren't relevant
                    ignoreUrls: [
                        /sockjs-node/, // WebSocket connections
                        /hot-update/, // HMR updates
                        /\/__vite_ping/, // Vite dev server
                    ],
                }),

                // XMLHttpRequest instrumentation for legacy AJAX calls
                new XMLHttpRequestInstrumentation({
                    propagateTraceHeaderCorsUrls: [
                        /http:\/\/localhost:5177.*/, // Backend API
                        /https?:\/\/.*/, // Allow all URLs
                    ],
                    // Add custom attributes for XHR requests
                    applyCustomAttributesOnSpan: (span, xhr) => {
                        console.log('🔍 OpenTelemetry: Instrumenting XHR request');
                        span.setAttribute('http.frontend', 'react-admin');
                        span.setAttribute('xhr.ready_state', xhr.readyState);
                    },
                }),
            ],
        });

        console.log('🔧 OpenTelemetry: Instrumentations registered');
        console.log('🎉 OpenTelemetry Network Tracing initialized for Transactor Admin Frontend');

        // Test telemetry is working by creating a manual span
        import('@opentelemetry/api').then(({ trace }) => {
            const tracer = trace.getTracer('transactor-admin-frontend');
            const span = tracer.startSpan('telemetry.initialization.test');
            span.setAttributes({
                'telemetry.test': true,
                'initialization.timestamp': Date.now(),
            });
            span.addEvent('telemetry.test.completed');
            span.end();
            console.log('✅ OpenTelemetry: Test span created and sent');
        });

    } catch (error) {
        console.error('❌ Failed to initialize OpenTelemetry:', error);
        console.error('Stack trace:', error.stack);
    }
};

// Custom hook for creating spans in React components
export const useTracing = () => {
    const { trace } = require('@opentelemetry/api');

    const createSpan = (name: string, attributes?: Record<string, any>) => {
        const tracer = trace.getTracer('transactor-admin-frontend');
        const span = tracer.startSpan(name);

        if (attributes) {
            Object.entries(attributes).forEach(([key, value]) => {
                span.setAttribute(key, value);
            });
        }

        return span;
    };

    return { createSpan };
}; 